import { useCallback, useState } from "react"
import useSWR from "swr"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants/value"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { PaginationInfo } from "src/components/tailwind-components/Pagination"
import {
  CreditContractPreparationStatus,
  CreditContractPreparationListResponse,
  UseCreditContractPreparationsParams,
  UseCreditContractPreparationsReturn,
} from "../types"

/**
 * ページ情報が取得できない場合に利用するデフォルトのページネーションを生成する。
 */
const createDefaultPagination = (page: number): PaginationInfo => ({
  currentPage: page,
  totalCount: 0,
  totalPages: 1,
})

/**
 * クレジット契約準備作業一覧を取得するカスタムフック。
 * ページネーションとステータスフィルタをクエリパラメータとして付加して API を叩く。
 */
export const useCreditContractPreparations = (
  params: UseCreditContractPreparationsParams = {}
): UseCreditContractPreparationsReturn => {
  const { getWithToken } = useSmoothApi()
  const currentPage = params.page ?? 1
  const defaultPagination = createDefaultPagination(currentPage)

  // URLにクエリパラメータを追加
  const searchParams = new URLSearchParams()
  searchParams.append("page", currentPage.toString())
  if (params.statuses && params.statuses.length > 0) {
    searchParams.append("statuses", params.statuses.join(","))
  }

  const baseUrl = `${SMOOTH_SMOOTHER_API_URL}/screen/credit_contract_preparation_list`
  const url = searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl

  // SWRキーはクエリパラメータを含むURLのみを使用し、不要な再フェッチを防ぐ
  const swrKey = url

  /**
   * SWR 用のフェッチャー。API から一覧データを取得する。
   */
  const fetcher = async () => {
    try {
      const res = await getWithToken(url)
      const data = res.data as CreditContractPreparationListResponse
      return data
    } catch (error) {
      console.error("クレジット契約準備作業一覧の取得に失敗しました:", error)
      throw error
    }
  }

  const creditContractPreparationsSwr = useSWR(swrKey, fetcher, {
    refreshInterval: 5000,
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
  })

  // データを強制的にリフレッシュする関数
  /**
   * SWR キャッシュを強制的に再検証する。
   */
  const refresh = async () => {
    return creditContractPreparationsSwr.mutate(undefined, { revalidate: true })
  }

  return {
    data: creditContractPreparationsSwr.data?.creditContractPreparations || [],
    pagination: creditContractPreparationsSwr.data?.pagination ?? defaultPagination,
    isLoading:
      (!creditContractPreparationsSwr.data ||
        creditContractPreparationsSwr.data.creditContractPreparations === undefined) &&
      !creditContractPreparationsSwr.error,
    error: creditContractPreparationsSwr.error,
    refresh,
  }
}

export const useUpdateCreditContractPreparationStatus = () => {
  const { patchWithToken } = useSmoothApi()
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateStatus = useCallback(
    async (id: number, status: CreditContractPreparationStatus) => {
      const endpoint = `${SMOOTH_SMOOTHER_API_URL}/screen/credit_contract_preparation_list/${id}/status`
      setIsUpdating(true)
      setError(null)

      try {
        const response = await patchWithToken(endpoint, { status })
        return response
      } catch (error: any) {
        console.error("クレジット契約準備のステータス更新に失敗しました:", error)

        if (error?.response?.data) {
          const message =
            error.response.data?.message ||
            error.response.data?.error ||
            "システムエラーが発生しました"
          const apiError = new Error(message)
          ;(apiError as any).response = error.response
          setError(apiError)
          throw apiError
        } else {
          const networkError = new Error("通信エラーが発生しました")
          setError(networkError)
          throw networkError
        }
      } finally {
        setIsUpdating(false)
      }
    },
    [patchWithToken]
  )

  return {
    updateStatus,
    isUpdating,
    error,
  }
}

// Helper to extract error message from API error response
const extractErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (error && typeof error === "object" && "response" in error) {
    const responseError = error as { response?: { data?: { message?: string; error?: string } } }
    return (
      responseError.response?.data?.message || responseError.response?.data?.error || defaultMessage
    )
  }
  if (error instanceof Error) {
    return error.message
  }
  return defaultMessage
}

export const useTriggerCreditContractPreparationAutoExtraction = () => {
  const { postWithToken } = useSmoothApi()
  const [isTriggering, setIsTriggering] = useState(false)

  const triggerAutoExtraction = useCallback(
    async (id: number) => {
      const endpoint = `${SMOOTH_SMOOTHER_API_URL}/screen/credit_contract_preparation_form/${id}/auto_extract`
      setIsTriggering(true)

      try {
        const response = await postWithToken(endpoint, {})
        return response
      } catch (error: unknown) {
        console.error("自動抽出のキュー投入に失敗しました:", error)
        const message = extractErrorMessage(error, "自動抽出のキュー投入に失敗しました")
        throw new Error(message)
      } finally {
        setIsTriggering(false)
      }
    },
    [postWithToken]
  )

  return { triggerAutoExtraction, isTriggering }
}
